import isEmpty from 'lodash/isEmpty';

import type { GenericDetails } from 'components/Input/Autocomplete';

import { MAX_LIMIT_SYMBOL_LENGTH } from 'constants/common';
import { QA_EMAILS } from 'constants/flow';

const isNullish = (value: unknown): value is null | undefined => value === null || value === undefined;

const toTitleCase = (str: string) =>
    str.replace('_', ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());

const cleanSearchParams = (params: object) => {
    return Object.fromEntries(
        Object.entries(params).filter(
            ([_key, value]) => value !== '' && value !== null && value !== undefined && (!Array.isArray(value) || value.length > 0)
        )
    );
};

const getCapitalize = (str: string) => {
    if (!str) return '';
    return str[0].toUpperCase() + str.substring(1);
};

const prepareFilterPayload = <T extends Record<string, GenericDetails | undefined>>(
    filter?: T,
    listKeyToUpdate: (keyof T)[] = [],
    listKeyToSkip?: (keyof T)[]
): Record<string, string> => {
    if (!filter) return {};

    return Object.entries(filter).reduce<Record<string, string>>((acc, [key, value]) => {
        if (!value || listKeyToSkip?.includes(key)) return acc;

        const { id, name } = value;
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        const text = id || name;
        if (!text) return acc;

        const keyFilter = listKeyToUpdate.includes(key) ? `${key}${id ? '_id' : '_name'}` : key;

        acc[keyFilter] = limitText(String(text), MAX_LIMIT_SYMBOL_LENGTH);

        return acc;
    }, {});
};

const getNames = (email: string | null): { name: string; surName: string } => {
    if (!email) {
        return { name: '', surName: '' };
    }

    const isAqaEmail = QA_EMAILS.includes(email);

    if (isAqaEmail) {
        return { name: 'Q', surName: 'A' };
    }

    const emailPrefix = email.split('@')[0] || '';
    const [firstName, lastName] = emailPrefix.split('.');

    return {
        name: firstName ? getCapitalize(firstName) : '',
        surName: lastName ? getCapitalize(lastName) : '',
    };
};

const getReplaceQuotes = (str: string) => {
    return str.replaceAll("'", '"');
};

const isValidJSON = (str?: string) => {
    if (!str) return false;

    let isValid;

    try {
        const parserJson: unknown = JSON.parse(str);
        if (!isEmpty(parserJson)) {
            isValid = true;
        } else {
            isValid = false;
        }
    } catch {
        isValid = false;
    }

    return isValid;
};

const isObject = (value: unknown): value is Record<string, unknown> => {
    return Boolean(value) && value !== null && typeof value === 'object' && value.constructor === Object;
};

const limitText = (text: string | undefined, limit = 0): string => {
    if (!text) return '';
    return text.length >= limit ? text.slice(0, limit) : text;
};

const kebabCaseToCamelCase = (value: string) => {
    return value.replace(/-([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());
};

export {
    toTitleCase,
    cleanSearchParams,
    getReplaceQuotes,
    getCapitalize,
    getNames,
    isNullish,
    isObject,
    isValidJSON,
    limitText,
    prepareFilterPayload,
    kebabCaseToCamelCase,
};
